/**
 * Jamaat times as RULES: periods with a time per prayer.  Owner, 2026-10-15.
 *
 * Until now a jamaat time could only come from a column of the year table (prayer_day *_jamaat) or a per-day override,
 * and no mosque in production had either: the app, the screens and the panel never showed a jamaat. A mosque thinks in
 * periods — «from 1 November: Fajr 07:15, Dhuhr 13:00, Maghrib 5 minutes after sunset» — so that is what is stored
 * (jamaat_period), by the mosque's staff in the panel, or read from IRN Bønnetider (apps/api/src/bonnetid.js).
 *
 * A value is either
 *   "HH:MM"   a fixed wall-clock time, or
 *   "+N"      N minutes (0…120) after that day's BEGINS time — Maghrib is commonly "+5".
 * Jumu'ah (khutbah, optional, and jamaat) is a fixed time only, and is applied on Fridays only.
 *
 * Wall-clock arithmetic only (prayer-times.js: a timetable time is never an instant). An offset is added to the day's
 * own begins string, so it follows the timetable across the clock change on its own: "+5" on a Maghrib of 18:05 the day
 * before the change and 17:03 the day after is 18:10 and 17:08, never an hour out.
 *
 * WHICH VALUE A DAY SHOWS, per prayer (the first that has one):
 *   1. a per-day override (prayer_override) — applied after this module, by prayer.resolveDay(); never changed here;
 *   2. a staff period covering the day;
 *   3. an IRN period covering the day;
 *   4. what the year table holds for the day (prayer_day *_jamaat), if anything;
 *   5. Jumu'ah only: the mosque's «Fast Jumu'ah-tid» (owner 2026-11-01; jamaat_jumua_fixed, one row) — a khutbah and a
 *      jamaat time for EVERY Friday, the lowest rule of all, so a period, the year table or a day's override still wins.
 *      It is handed in among the periods as one pseudo-period of source 'fixed' (fixedPeriod()) and used as a pair:
 *      only on a Friday no higher rule gave a Jumu'ah jamaat, and then its khutbah too where none was given.
 * Between periods of one source the more specific wins: the shorter period, then the later start (IRN may send a
 * winter period and a Ramadan period that overlaps it; staff periods may not overlap — validate() refuses it).
 *
 * NEVER BEFORE BEGINS: a computed jamaat earlier than its prayer's begins time that day (overrides.jamaatAfterBegins —
 * Isha after midnight included) is not shown. It is FLAGGED, and the next rule down is asked instead. A staff period
 * that would do this on a day already in the timetable is refused when it is saved (the API, with checkAgainst()).
 * NOR AFTER THE WINDOW (audit F-01, 2026-10-19): the same for a jamaat at or after its prayer's time runs out
 * (overrides.jamaatInWindow — Fajr at sunrise, Dhuhr/Jumu'ah at Asr, Asr at Maghrib, Maghrib at Isha, Isha at the next
 * Fajr). cjas's IRN period kept Fajr at 07:30 across the clock change while sunrise moved to 07:21: flagged
 * 'after_window', the day falls back to the next rule down — usually none, so every surface counts to Fajr BEGINS.
 *
 * Pure: no database, no clock. The API reads the periods (apps/api/src/jamaat.js) and every reader — the app, the TV,
 * the WordPress plugin, the panel — gets the result through serialize.prayerDay(), which calls resolve() here.
 */
'use strict';

const prayer = require('./prayer-times');
const overrides = require('./overrides');

const PRAYERS = prayer.PRAYERS;                                       // fajr dhuhr asr maghrib isha
const JUMUA = ['jumua_khutbah', 'jumua_jamaat'];
const FIELDS = [...PRAYERS, ...JUMUA];
const SOURCES = ['staff', 'irn'];
const RANK = { staff: 0, irn: 1, fixed: 2 };
/** The «Fast Jumu'ah-tid» as a pseudo-period (fixedPeriod()): never a jamaat_period row, never in gaps() or validate(). */
const FIXED = 'fixed';
const FIXED_ID = 'fixed-jumua';
const MAX_OFFSET = 120;
/** A period of at most two years: a typo in a year ("2062") is refused rather than stored for decades. */
const MAX_DAYS = 731;
const LABEL_MAX = 60;
const LABEL_NO = { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha', jumua_khutbah: 'Khutbah', jumua_jamaat: "Jumu'ah" };

const pad = (n) => String(n).padStart(2, '0');
const isTime = (v) => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(v || ''));
const isOffset = (v) => /^\+\d{1,3}$/.test(String(v || '')) && Number(String(v).slice(1)) <= MAX_OFFSET;
/** A real calendar date "YYYY-MM-DD" (not 2026-02-30). */
function isDate(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || ''));
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}
const dayNo = (iso) => Math.round(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86400000);
const isoOfDay = (n) => new Date(n * 86400000).toISOString().slice(0, 10);
const addDays = (iso, k) => isoOfDay(dayNo(iso) + k);
const isFriday = (iso) => new Date(`${iso}T12:00:00Z`).getUTCDay() === 5;
const span = (p) => dayNo(p.ends_on) - dayNo(p.starts_on) + 1;

/**
 * One value as typed or received → { ok, value } with value "HH:MM", "+N" or null (empty). Accepts "7:15", "07.15",
 * "+5", "+ 5", "+5 min". `fixedOnly` (Jumu'ah) refuses an offset.
 */
function parseValue(v, { fixedOnly = false } = {}) {
  if (v === null || v === undefined) return { ok: true, value: null };
  const s = String(v).trim();
  if (s === '') return { ok: true, value: null };
  const off = /^\+\s*(\d{1,3})\s*(min)?$/i.exec(s);
  if (off) {
    if (fixedOnly) return { ok: false, code: 'offset_not_allowed' };
    const n = Number(off[1]);
    return n <= MAX_OFFSET ? { ok: true, value: `+${n}` } : { ok: false, code: 'offset_range' };
  }
  const t = /^(\d{1,2})[:.](\d{2})$/.exec(s);
  if (t && Number(t[1]) <= 23 && Number(t[2]) <= 59) return { ok: true, value: `${pad(Number(t[1]))}:${t[2]}` };
  return { ok: false, code: 'bad_value' };
}

const MESSAGES = {
  bad_value: (f) => `${LABEL_NO[f]}: skriv et klokkeslett (TT:MM) eller minutter etter at bønnen begynner (+5).`,
  offset_range: (f) => `${LABEL_NO[f]}: høyst +${MAX_OFFSET} minutter etter at bønnen begynner.`,
  offset_not_allowed: (f) => `${LABEL_NO[f]}: skriv et klokkeslett (TT:MM).`,
};

/**
 * One period → { ok, errors, warnings, period } with the period normalised (canonical values, null for empty).
 * Input keys are the stored ones: starts_on, ends_on, fajr … isha, jumua_khutbah, jumua_jamaat, label.
 */
function validatePeriod(input = {}) {
  const errors = [], warnings = [];
  const p = { starts_on: String(input.starts_on || ''), ends_on: String(input.ends_on || '') };
  if (!isDate(p.starts_on)) errors.push({ field: 'starts_on', code: 'bad_date', message: 'Fra-dato mangler eller er ugyldig.' });
  if (!isDate(p.ends_on)) errors.push({ field: 'ends_on', code: 'bad_date', message: 'Til-dato mangler eller er ugyldig.' });
  if (isDate(p.starts_on) && isDate(p.ends_on)) {
    if (p.ends_on < p.starts_on) errors.push({ field: 'ends_on', code: 'range', message: 'Til-dato er før fra-dato.' });
    else if (span(p) > MAX_DAYS) errors.push({ field: 'ends_on', code: 'too_long', message: 'En periode kan være høyst to år.' });
  }
  for (const f of FIELDS) {
    const r = parseValue(input[f], { fixedOnly: JUMUA.includes(f) });
    if (!r.ok) errors.push({ field: f, code: r.code, message: MESSAGES[r.code](f) });
    p[f] = r.ok ? r.value : null;
  }
  const label = input.label == null ? '' : String(input.label).trim();
  if (label.length > LABEL_MAX) errors.push({ field: 'label', code: 'too_long', message: `Navnet kan være høyst ${LABEL_MAX} tegn.` });
  p.label = label || null;
  if (!errors.length && !FIELDS.some((f) => p[f])) errors.push({ field: 'fajr', code: 'empty', message: 'Fyll inn minst én jamaat-tid.' });
  if (p.jumua_khutbah && p.jumua_jamaat && p.jumua_khutbah > p.jumua_jamaat) {
    errors.push({ field: 'jumua_khutbah', code: 'khutbah_after_jamaat', message: "Khutbah kan ikke være etter Jumu'ah-jamaat." });
  }
  // Sunset moves about two minutes a day here: a fixed Maghrib over more than a week is almost always meant as "+N".
  if (isTime(p.maghrib) && !errors.length && span(p) > 7) {
    warnings.push({ field: 'maghrib', code: 'maghrib_fixed', message: 'Maghrib flytter seg med solnedgangen. Vanligvis skrives den som minutter etter (+5).' });
  }
  return { ok: errors.length === 0, errors, warnings, period: p };
}

const overlapping = (a, b) => a.starts_on <= b.ends_on && b.starts_on <= a.ends_on;
/** Periods of the same source as `candidate` that share a day with it (itself excluded by id). */
function overlaps(periods, candidate) {
  return (periods || []).filter((p) => p.source === candidate.source && p.id !== candidate.id && overlapping(p, candidate));
}

/**
 * A set of periods: each valid, and no two STAFF periods sharing a day (which of two equal rules applies is a question
 * nobody should have to ask). IRN periods may overlap: the more specific one wins (see the top of this file).
 */
function validate(periods) {
  const errors = [];
  (periods || []).forEach((p, index) => {
    const v = validatePeriod(p);
    for (const e of v.errors) errors.push({ ...e, index, id: p.id || null });
    if (!SOURCES.includes(p.source)) errors.push({ field: 'source', code: 'bad_source', index, id: p.id || null, message: 'Ukjent kilde.' });
  });
  const staff = (periods || []).map((p, index) => ({ ...p, index })).filter((p) => p.source === 'staff');
  for (let i = 0; i < staff.length; i++) for (let j = i + 1; j < staff.length; j++) {
    if (overlapping(staff[i], staff[j])) errors.push({ field: 'starts_on', code: 'overlap', index: staff[j].index, id: staff[j].id || null,
      message: `Perioden overlapper ${staff[i].starts_on} – ${staff[i].ends_on}.` });
  }
  return { ok: errors.length === 0, errors };
}

/** The value a rule gives one prayer on a day with that begins time; null when it gives none (or cannot: no begins). */
function valueOn(raw, begins) {
  if (!raw) return null;
  if (isTime(raw)) return raw;
  if (!isOffset(raw) || !isTime(begins)) return null;
  return prayer.toHHMM((prayer.toMinutes(begins) + Number(raw.slice(1))) % 1440);
}

/** The periods that cover `date`, most binding first: staff before IRN, then the shorter, then the later start. */
function on(periods, date) {
  return (periods || []).filter((p) => p.starts_on <= date && date <= p.ends_on).sort((a, b) =>
    (RANK[a.source] ?? 9) - (RANK[b.source] ?? 9) || span(a) - span(b) || b.starts_on.localeCompare(a.starts_on)
    || String(a.id || '').localeCompare(String(b.id || '')));
}

/** Would `value` for `field` be before its prayer begins on `day` (a prayer_day row)? */
function beforeBegins(field, value, day) {
  if (!value || !day) return false;
  if (field === 'jumua_jamaat') return isTime(day.dhuhr_begins) && value < day.dhuhr_begins;
  if (field === 'jumua_khutbah') return false;   // a bayan/khutbah may start before Dhuhr; only the prayer is held to it
  return overrides.jamaatAfterBegins({ [`${field}_jamaat`]: value }, day).length > 0;
}

/**
 * Would `value` for `field` be at or after its prayer's time runs out on `day` (F-01)? → the window's end ("HH:MM"),
 * or null when it is inside (or there is nothing to compare). Jumu'ah is held to Dhuhr's window; a khutbah is not held.
 * `nextDay` (the next prayer_day row) gives Isha its real end; without it the row's own Fajr stands in.
 */
function afterWindow(field, value, day, nextDay = null) {
  if (!value || !day || field === 'jumua_khutbah') return null;
  const p = field === 'jumua_jamaat' ? 'dhuhr' : field;
  const e = overrides.jamaatInWindow({ [`${p}_jamaat`]: value }, day, nextDay)[0];
  return e ? e.ends : null;
}
const COL = (f) => (JUMUA.includes(f) ? f : `${f}_jamaat`);

/**
 * One timetable day (a prayer_day row: date, *_begins, *_jamaat) with the periods applied. Returns
 *   row      the row with each *_jamaat / jumua_* replaced where a period gives a value (begins never touched)
 *   sources  per field: 'staff' | 'irn' | 'timetable' (the year table's own value) | 'fixed' (Jumu'ah: «Fast Jumu'ah-tid»)
 *            | null (none)
 *   flags    [{ date, field, code: 'before_begins' | 'after_window', value, begins, ends?, source, periodId }] — values
 *            NOT shown
 * `nextDay` (optional, the next prayer_day row) is only for Isha's window end (afterWindow).
 */
function onto(day, periods, nextDay = null) {
  if (!day) return { row: day, sources: {}, flags: [] };
  const date = String(day.date);
  const fixed = (periods || []).find((p) => p && p.source === FIXED) || null;
  const covering = on((periods || []).filter((p) => p && p.source !== FIXED), date);
  const row = { ...day }, sources = {}, flags = [];
  for (const f of FIELDS) {
    const col = COL(f);
    let chosen = null;
    if (!JUMUA.includes(f) || isFriday(date)) {
      for (const p of covering) {
        const v = valueOn(p[f], JUMUA.includes(f) ? null : day[`${f}_begins`]);   // Jumu'ah: fixed times only
        if (!v) continue;
        if (beforeBegins(f, v, day)) {
          flags.push({ date, field: f, code: 'before_begins', value: v, begins: f === 'jumua_jamaat' ? day.dhuhr_begins : day[`${f}_begins`],
            source: p.source, periodId: p.id || null });
          continue;
        }
        const ends = afterWindow(f, v, day, nextDay);
        if (ends) {
          flags.push({ date, field: f, code: 'after_window', value: v, begins: f === 'jumua_jamaat' ? day.dhuhr_begins : day[`${f}_begins`],
            ends, source: p.source, periodId: p.id || null });
          continue;
        }
        chosen = { v, source: p.source };
        break;
      }
    }
    if (chosen) { row[col] = chosen.v; sources[f] = chosen.source; }
    else sources[f] = day[col] ? 'timetable' : null;
  }
  // 5. «Fast Jumu'ah-tid»: only on a Friday nothing above gave a Jumu'ah jamaat; held to the same two rules.
  if (fixed && isFriday(date) && !sources.jumua_jamaat && isTime(fixed.jumua_jamaat)) {
    const v = fixed.jumua_jamaat;
    const flag = { date, field: 'jumua_jamaat', value: v, begins: day.dhuhr_begins, source: FIXED, periodId: fixed.id || FIXED_ID };
    const early = beforeBegins('jumua_jamaat', v, day);
    const ends = early ? null : afterWindow('jumua_jamaat', v, day, nextDay);
    if (early) flags.push({ ...flag, code: 'before_begins' });
    else if (ends) flags.push({ ...flag, code: 'after_window', ends });
    else {
      row.jumua_jamaat = v; sources.jumua_jamaat = FIXED;
      if (!sources.jumua_khutbah && isTime(fixed.jumua_khutbah)) { row.jumua_khutbah = fixed.jumua_khutbah; sources.jumua_khutbah = FIXED; }
    }
  }
  return { row, sources, flags };
}

/**
 * The «Fast Jumu'ah-tid» (a jamaat_jumua_fixed row, or { jumua_khutbah, jumua_jamaat }) → the pseudo-period every
 * resolver takes among the periods, or null when no Jumu'ah jamaat is set.
 */
function fixedPeriod(setting) {
  if (!setting || !isTime(setting.jumua_jamaat)) return null;
  return { id: FIXED_ID, source: FIXED, starts_on: '0001-01-01', ends_on: '9999-12-31',
    fajr: null, dhuhr: null, asr: null, maghrib: null, isha: null,
    jumua_khutbah: isTime(setting.jumua_khutbah) ? setting.jumua_khutbah : null, jumua_jamaat: setting.jumua_jamaat };
}

/**
 * A «Fast Jumu'ah-tid» as typed → { ok, errors, value: { jumua_khutbah, jumua_jamaat } }. The jamaat is required and a
 * fixed time (never "+N"); the khutbah is optional and not after the jamaat.
 */
function validateFixedJumua(input = {}) {
  const errors = [], value = {};
  for (const f of JUMUA) {
    const raw = input[f] !== undefined ? input[f] : input[f === 'jumua_jamaat' ? 'jamaat' : 'khutbah'];
    const r = parseValue(raw, { fixedOnly: true });
    if (!r.ok) errors.push({ field: f, code: r.code, message: MESSAGES[r.code](f) });
    value[f] = r.ok ? r.value : null;
  }
  if (!errors.length && !value.jumua_jamaat) errors.push({ field: 'jumua_jamaat', code: 'empty', message: "Skriv Jumu'ah-jamaat (TT:MM)." });
  if (!errors.length && value.jumua_khutbah && value.jumua_khutbah > value.jumua_jamaat) {
    errors.push({ field: 'jumua_khutbah', code: 'khutbah_after_jamaat', message: "Khutbah kan ikke være etter Jumu'ah-jamaat." });
  }
  return { ok: errors.length === 0, errors, value };
}

/**
 * A «Fast Jumu'ah-tid» checked against the Fridays the timetable already has (as checkAgainst() for a period): every
 * Friday its jamaat would be before Dhuhr begins or at/after Asr begins. `days` in date order.
 */
function checkFixedJumua(setting, days) {
  const p = fixedPeriod(setting);
  if (!p) return [];
  const out = [];
  (days || []).forEach((d, i) => {
    if (!isFriday(String(d.date))) return;
    const plain = { ...d, jumua_jamaat: null, jumua_khutbah: null };   // asked as if nothing above gave one
    for (const fl of onto(plain, [p], nextOf(days, i)).flags) out.push({ ...fl, message: `${d.date}: ${flagText(fl)}` });
  });
  return out;
}

/** Every day of `days` (prayer_day rows) with the periods applied: [{ date, jamaat: {field: value}, sources, flags }]. */
function apply(periods, days) {
  return (days || []).map((d, i) => {
    const r = onto(d, periods, nextOf(days, i));
    return { date: d.date, jamaat: Object.fromEntries(FIELDS.map((f) => [f, r.row[COL(f)] || null])), sources: r.sources, flags: r.flags };
  });
}

/**
 * The day as every reader gets it: periods applied, then the per-day override on top (prayer.resolveDay — the
 * override wins, as it always has). `override` is a prayer_override row (is_closed as a boolean) or null.
 * Returns { resolved (prayer.resolveDay's day), sources (per field, 'override' where the override set it), flags }.
 */
function resolve(day, override, periods, nextDay = null) {
  if (!day) return { resolved: null, sources: {}, flags: [] };
  const { row, sources, flags } = onto(day, periods, nextDay);
  const resolved = prayer.resolveDay(row, override);
  const out = { ...sources };
  if (override) for (const f of FIELDS) { const v = override[COL(f)]; if (v != null && v !== '') out[f] = 'override'; }
  return { resolved, sources: out, flags };
}

/** The row after days[i] when it is the next calendar day, else null (a gap in the timetable is no next day). */
function nextOf(days, i) {
  const d = days && days[i], n = days && days[i + 1];
  return d && n && String(n.date) === addDays(String(d.date), 1) ? n : null;
}

/** What a flag says in the panel's words: before its prayer begins, or after its time is out (F-01). */
function flagText(fl) {
  const prayerName = fl.field === 'jumua_jamaat' ? 'Dhuhr' : LABEL_NO[fl.field];
  if (fl.code === 'after_window') return `${LABEL_NO[fl.field]}-jamaat ${fl.value} er ikke før ${prayerName}-tiden er ute (${fl.ends}).`;
  return `${LABEL_NO[fl.field]}-jamaat ${fl.value} er før ${prayerName} begynner (${fl.begins}).`;
}

/**
 * A staff period checked against the days the timetable already has: every day on which one of its values would be
 * before its prayer begins, or at/after its time runs out (F-01) — with the day, so the panel can say which. The API
 * refuses to save while this is not empty. `days` in date order (the next day gives Isha its end).
 */
function checkAgainst(period, days) {
  const p = { ...period, source: 'staff', id: period.id || '__candidate' };
  const out = [];
  (days || []).forEach((d, i) => {
    if (d.date < p.starts_on || d.date > p.ends_on) return;
    for (const fl of onto(d, [p], nextOf(days, i)).flags) out.push({ ...fl, message: `${d.date}: ${flagText(fl)}` });
  });
  return out;
}

/**
 * The clock changes that fall inside `[from, to]` in `zone`: [{ date, deltaMinutes }] — the date whose wall clock
 * differs from the day before by deltaMinutes (+60 spring, −60 autumn). Intl only, noon to noon, so the hour of the
 * change itself never matters. An unknown zone has none.
 */
function clockChanges(from, to, zone) {
  if (!isDate(from) || !isDate(to) || to < from) return [];
  let fmt;
  try { fmt = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }); }
  catch { return []; }
  const wallAtNoonUtc = (iso) => {
    const o = {};
    for (const x of fmt.formatToParts(new Date(`${iso}T12:00:00Z`))) o[x.type] = x.value;
    return (Number(o.hour) % 24) * 60 + Number(o.minute);
  };
  const out = [];
  let prev = wallAtNoonUtc(from);
  for (let n = dayNo(from) + 1; n <= dayNo(to); n++) {
    const iso = isoOfDay(n), w = wallAtNoonUtc(iso);
    if (w !== prev) out.push({ date: iso, deltaMinutes: w - prev });
    prev = w;
  }
  return out;
}

/**
 * IRN periods that hold a FIXED time ("07:30", not "+N") across a clock change (F-01): the sun's times jump an hour
 * on that night and a fixed jamaat does not, so a period that was right in summer may be outside its prayer from the
 * Sunday on. Staff are told before it happens; the day-by-day check (onto) still keeps a wrong value off the screens.
 * → [{ periodId, change (date), deltaMinutes, fields }], only for changes on or after `today`.
 */
function fixedAcrossClockChange(periods, today, zone) {
  const out = [];
  for (const p of periods || []) {
    if (p.source !== 'irn' || !isDate(p.starts_on) || !isDate(p.ends_on) || p.ends_on < today) continue;
    const fields = [...PRAYERS, 'jumua_jamaat'].filter((f) => isTime(p[f]));
    if (!fields.length) continue;
    const from = p.starts_on > today ? p.starts_on : today;
    for (const c of clockChanges(addDays(from, -1), p.ends_on, zone)) {
      if (c.date < from || c.date <= p.starts_on) continue;
      out.push({ periodId: p.id || null, change: c.date, deltaMinutes: c.deltaMinutes, fields });
    }
  }
  return out;
}

/** The days in [from, to] no period covers, as ranges [{ from, to, days }] — only meaningful once a period exists. */
function gaps(periods, from, to) {
  periods = (periods || []).filter((p) => p && p.source !== FIXED);
  if (!periods.length || !isDate(from) || !isDate(to) || to < from) return [];
  const out = [];
  let open = null;
  for (let n = dayNo(from); n <= dayNo(to); n++) {
    const d = isoOfDay(n);
    const covered = periods.some((p) => p.starts_on <= d && d <= p.ends_on);
    if (!covered && !open) open = { from: d, to: d };
    else if (!covered) open.to = d;
    else if (open) { out.push(open); open = null; }
  }
  if (open) out.push(open);
  return out.map((g) => ({ ...g, days: dayNo(g.to) - dayNo(g.from) + 1 }));
}

/** A value in the panel's words: "07:15" or "5 min etter". */
function describe(v) {
  if (!v) return '—';
  return isOffset(v) ? `${Number(v.slice(1))} min etter` : v;
}

module.exports = {
  PRAYERS, JUMUA, FIELDS, SOURCES, FIXED, FIXED_ID, MAX_OFFSET, MAX_DAYS, LABEL_MAX, LABEL_NO,
  isTime, isOffset, isDate, addDays, isFriday, parseValue, validatePeriod, overlaps, validate, valueOn, on, onto, apply, resolve,
  afterWindow, flagText, checkAgainst, fixedPeriod, validateFixedJumua, checkFixedJumua, clockChanges, fixedAcrossClockChange, gaps, describe,
};
